/**
 * Locale registry — quy tắc THUẦN cho 2 khoá cấu hình locale (US-AE2.F5-0007 · @UC9012).
 *
 * Hai khoá system-only (group_overridable=false): `supported_locales` (mảng locale) và
 * `default_locale` (locale đơn). Resolver (config-resolver.ts) đã xử lý group_overridable=false
 * ⇒ KHÔNG đụng tới ở đây. File này chỉ là rule biên: value hợp lệ TRƯỚC khi ghi (config gate).
 *
 * MVP: bundle ngôn ngữ = ['vi','en'] — bộ locale có bundle i18n thật. AE10 (CI i18n gate) CHƯA
 * dựng ⇒ reject mọi locale ngoài bundle với 422 cho tới khi AE10 mở rộng bundle.
 *
 * Pure (không framework, không DB): ném DomainError mang `code` ổn định → API map sang 422.
 */
import { DomainError } from "./errors.js";

/**
 * Bộ ngôn ngữ có bundle i18n (AE10-bundle set). MVP = ['vi','en'].
 * Mở rộng khi AE10 thêm bundle ngôn ngữ mới (CI i18n gate đảm bảo có file dịch).
 */
export const SUPPORTED_LOCALE_BUNDLE = ["vi", "en"] as const;

export type BundleLocale = (typeof SUPPORTED_LOCALE_BUNDLE)[number];

/** Hai khoá cấu hình locale (system-only). */
export const LOCALE_KEYS = ["supported_locales", "default_locale"] as const;

export type LocaleKey = (typeof LOCALE_KEYS)[number];

/** True nếu key là một trong hai khoá locale. */
export function isLocaleKey(key: string): key is LocaleKey {
  return (LOCALE_KEYS as readonly string[]).includes(key);
}

function isInBundle(locale: unknown): locale is BundleLocale {
  return (
    typeof locale === "string" && (SUPPORTED_LOCALE_BUNDLE as readonly string[]).includes(locale)
  );
}

/**
 * `supported_locales` phải là mảng KHÔNG rỗng các string, mỗi phần tử ∈ SUPPORTED_LOCALE_BUNDLE.
 * - không phải mảng / rỗng → SUPPORTED_LOCALES_EMPTY.
 * - phần tử ngoài bundle (hoặc không phải string) → LOCALE_NOT_IN_BUNDLE.
 */
export function validateSupportedLocales(value: unknown): void {
  if (!Array.isArray(value) || value.length === 0) {
    throw new DomainError("SUPPORTED_LOCALES_EMPTY");
  }
  for (const locale of value) {
    if (!isInBundle(locale)) {
      throw new DomainError("LOCALE_NOT_IN_BUNDLE");
    }
  }
}

/**
 * `default_locale` phải là string ∈ `supportedLocales` (tập supported hiện hành hoặc mới).
 * Không phải string / ngoài supported → DEFAULT_LOCALE_NOT_SUPPORTED.
 */
export function validateDefaultLocale(value: unknown, supportedLocales: readonly unknown[]): void {
  if (typeof value !== "string" || !supportedLocales.includes(value)) {
    throw new DomainError("DEFAULT_LOCALE_NOT_SUPPORTED");
  }
}

/**
 * Nguồn CHÂN LÝ DUY NHẤT cho tập locale đang hỗ trợ (US-AE7.F6-0160 S02/S08, @UC9168/@UC9175).
 *
 * PURE: nhận `settingValue` (giá trị đọc từ `system_settings.supported_locales`) — KHÔNG chạm DB.
 * Trước đây worker chỉ đọc `default_locale`, còn `supported_locales` là DEAD-CONFIG (đổi trong admin
 * không tác dụng gì). Resolver này là chỗ để dispatcher/render đọc THẬT.
 *
 * FAIL-SAFE: value thiếu/sai (null, không mảng, rỗng, phần tử ngoài bundle) ⇒ trả nguyên
 * `SUPPORTED_LOCALE_BUNDLE` (không ném) — locale là hạ tầng gửi thông báo, thà rộng còn hơn chặn
 * hết. Chỉ giữ các phần tử ∈ bundle (bỏ giá trị lạ) để không bao giờ trả locale không có i18n.
 */
export function resolveSupportedLocales(settingValue: unknown): readonly BundleLocale[] {
  if (!Array.isArray(settingValue) || settingValue.length === 0) {
    return SUPPORTED_LOCALE_BUNDLE;
  }
  const filtered = settingValue.filter(isInBundle);
  return filtered.length > 0 ? filtered : SUPPORTED_LOCALE_BUNDLE;
}

/**
 * Nguồn CHÂN LÝ DUY NHẤT cho `default_locale` hệ thống (US-E24.F53-0571, review PR #638).
 *
 * PURE: nhận giá trị đọc từ `system_settings.default_locale` + tập supported đã resolve.
 *
 * 🔴 VÌ SAO TÁCH RA: phép kẹp này (default rớt khỏi supported ⇒ lấy phần tử đầu supported) trước đây
 * nằm gõ tay trong `apps/worker/.../batch-config.ts:loadDefaultLocale`. Khi đường nhắc inline của
 * `apps/api` cần đúng phép kẹp đó, lựa chọn là chép bản thứ hai — mà "hai bản chép của một luật rồi
 * trôi ra xa nhau" chính là lỗi task này sinh ra để dẹp. Query vẫn của từng surface (worker đã cắt dep
 * `@goppy/api`); chỉ LUẬT ở đây.
 */
export function resolveDefaultLocale(
  settingValue: unknown,
  supported: readonly BundleLocale[],
): BundleLocale {
  if (typeof settingValue === "string" && (supported as readonly string[]).includes(settingValue)) {
    return settingValue as BundleLocale;
  }
  return supported[0] ?? SUPPORTED_LOCALE_BUNDLE[0];
}

/**
 * Chọn locale để gửi cho 1 người nhận: dùng `preferred` nếu ∈ supported, ngược lại fallback `default`
 * (đảm bảo `default` cũng ∈ supported, nếu không lấy phần tử đầu supported). Không bao giờ trả locale
 * ngoài `supported`.
 */
export function pickRecipientLocale(
  preferred: string | null | undefined,
  defaultLocale: string,
  supported: readonly BundleLocale[],
): BundleLocale {
  if (preferred != null && (supported as readonly string[]).includes(preferred)) {
    return preferred as BundleLocale;
  }
  if ((supported as readonly string[]).includes(defaultLocale)) {
    return defaultLocale as BundleLocale;
  }
  return supported[0] ?? SUPPORTED_LOCALE_BUNDLE[0];
}

/**
 * Bất biến nhất quán: default ∈ supported. Bao trùm ca "đổi supported làm rớt default hiện tại"
 * (khi supported thay đổi). Kiểm supported hợp lệ trước (rỗng/ngoài bundle), rồi default-membership.
 */
export function validateLocaleConsistency(nextSupported: unknown, nextDefault: unknown): void {
  validateSupportedLocales(nextSupported);
  validateDefaultLocale(nextDefault, nextSupported as readonly unknown[]);
}

/**
 * Bất biến system-only (H1): khoá locale KHÔNG được phép `group_overridable=true`. Resolver C4 dựa
 * vào group_overridable=false để chốt locale là cấu hình hệ thống (personal locale = users.locale,
 * KHÔNG phải group config). Đặt group_overridable=true ⇒ LOCALE_SYSTEM_ONLY (422). Đặt false (hoặc
 * giữ nguyên) hợp lệ ⇒ không ném.
 */
export function assertLocaleSystemOnly(nextGroupOverridable: boolean): void {
  if (nextGroupOverridable === true) {
    throw new DomainError("LOCALE_SYSTEM_ONLY");
  }
}
